import React, { FC, useContext } from "react";
import { HashRouter, Redirect, Route, Switch } from "react-router-dom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrandVariants, createDarkTheme, createLightTheme, FluentProvider, Theme } from "@fluentui/react-components";

import { AIPage } from "@/pages/AIPage";
import { LoginPage } from "@/pages/LoginPage";
import { WorkflowPage } from "@/pages/WorkflowPage";
import { CequenceLogin } from "@/pages/CequenceLogin";
import { ThreadPage } from "@/pages/Thread/ThreadPage";
import { AuthContext, AuthContextProvider } from "@/utils/auth";
import { ToastContainer } from "@/components/ui/Feedback/Toast";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { EnvironmentContextProvider } from "@/utils/environment";

import { PusherContextProvider } from "./contexts/PusherContext";
import { OfflineContextProvider } from "./contexts/OfflineContext";
import { ChatbotProvider } from "./components/Chatbot/contexts/ChatbotContext";

const Router = HashRouter as any;
const RouterSwitch = Switch as any;
const RouterRoute = Route as any;
const RouterRedirect = Redirect as any;

const queryClient = new QueryClient();

// Custom brand variants
const myNewTheme: BrandVariants = { 
  10: "#00040A",
  100: "#5494FF",
  110: "#7FAFFF",
  120: "#A9C9FF",
  130: "#BFD7FF",
  140: "#D4E4FF",
  150: "#EAF2FF",
  160: "#F9FBFF",
  20: "#041023",
  30: "#831B3B",
  40: "#0C2754",
  50: "#10336C",
  60: "#194A9D",
  70: "#2162CE",
  80: "#2979FF",
  90: "#3982F9"
};

const lightTheme: Theme = {
  ...createLightTheme(myNewTheme),
  fontFamilyBase: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"
};

const darkTheme: Theme = {
  ...createDarkTheme(myNewTheme),
  fontFamilyBase: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"
};

// Apply custom foreground colors for dark theme
darkTheme.colorBrandForeground1 = myNewTheme[110];
darkTheme.colorBrandForeground2 = myNewTheme[120];

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <FluentProvider theme={lightTheme}>
        <OfflineContextProvider>
          <AppContent />
        </OfflineContextProvider>
      </FluentProvider>
    </QueryClientProvider>
  );
}

const AppContent = () => {
  return (
    <EnvironmentContextProvider>
      <AuthContextProvider>
        <PusherContextProvider>
          <ChatbotProvider>
            <Router>
              <RouterSwitch>
                <RouterRoute exact path="/login">
                  <LoginPage />
                </RouterRoute>
                <RouterRoute exact path="/cequence-login">
                  <CequenceLogin />
                </RouterRoute>
                <PrivateRoute>
                  <RouterSwitch>
                    <RouterRoute exact path="/">
                      <ThreadPage />
                    </RouterRoute>
                    <RouterRoute exact path="/ai">
                      <AIPage />
                    </RouterRoute>
                    <RouterRoute path="/workflows/:id">
                      <WorkflowPage />
                    </RouterRoute>
                  </RouterSwitch>
                </PrivateRoute>
              </RouterSwitch>
            </Router>
          </ChatbotProvider>
        </PusherContextProvider>
        <ToastContainer />
      </AuthContextProvider>
    </EnvironmentContextProvider>
  );
};

export const PrivateRoute: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useContext(AuthContext);

  let content = children;

  if (!user) {
    content = <RouterRedirect to={{ pathname: "/login" }} />;
  }

  return <RouterRoute>{content}</RouterRoute>;
};
